import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, Mail } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, expired, error
  const [message, setMessage] = useState('');
  const [userName, setUserName] = useState('');
  const hasVerified = React.useRef(false);

  useEffect(() => {
    if (hasVerified.current) return;
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Lien de vérification invalide. Aucun token fourni.');
      return;
    }

    hasVerified.current = true;

    const doVerify = async () => {
      try {
        const response = await axios.post(`${API}/auth/verify-email`, { token });
        if (response.data.success) {
          setStatus('success');
          setUserName(response.data.user_name || '');
          setMessage(response.data.message || 'Votre email a été vérifié avec succès !');
        } else {
          setStatus('error');
          setMessage(response.data.message || 'Une erreur est survenue.');
        }
      } catch (error) {
        const errorDetail = error.response?.data?.detail || '';
        if (errorDetail.includes('expiré') || errorDetail.includes('expired')) {
          setStatus('expired');
          setMessage('Ce lien de vérification a expiré (validité 48h). Veuillez vous réinscrire.');
        } else if (errorDetail.includes('invalide') || errorDetail.includes('invalid') || errorDetail.includes('valide')) {
          setStatus('error');
          setMessage('Ce lien de vérification n\'est pas valide ou a déjà été utilisé.');
        } else {
          setStatus('error');
          setMessage(errorDetail || 'Une erreur est survenue lors de la vérification.');
        }
      }
    };

    doVerify();
  }, [searchParams]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <div className="w-10 h-10 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900 mb-4">Vérification en cours...</CardTitle>
            <p className="text-gray-600">Veuillez patienter pendant que nous vérifions votre email.</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-green-700 mb-4">Email vérifié !</CardTitle>
            {userName && <p className="text-lg text-gray-700 mb-2">Bonjour {userName},</p>}
            <p className="text-gray-600 mb-6">{message}</p>
            <Button
              onClick={() => navigate('/login')}
              className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3"
              data-testid="go-to-login"
            >
              Se connecter
            </Button>
          </div>
        );

      case 'expired':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-12 h-12 text-amber-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-amber-700 mb-4">Lien expiré</CardTitle>
            <p className="text-gray-600 mb-6">{message}</p>
            <Button
              onClick={() => navigate('/register')}
              className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3"
              data-testid="go-to-register"
            >
              S'inscrire à nouveau
            </Button>
          </div>
        );

      case 'error':
      default:
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-red-700 mb-4">Erreur de vérification</CardTitle>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/login')}
                className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 w-full"
                data-testid="go-to-login"
              >
                Aller à la connexion
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full"
                data-testid="go-to-home"
              >
                Retour à l'accueil
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-teal-50 to-cyan-50">
      <Card className="w-full max-w-md shadow-xl" data-testid="verify-email-page">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_babidoc-health/artifacts/7a0rukid_HUMMM0fc84adc-81cc-4751-9a5e-2c9327247823.png" 
              alt="BabiDoc" 
              className="w-16 h-16 object-cover rounded-full"
            />
          </div>
        </CardHeader>
        <CardContent>
          {renderContent()}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmailPage;
